import { useMemo, useCallback } from 'react';
import { v4 as uuid_v4 } from 'uuid';
import { useAppDispatch } from 'src/store';
import { useGetUser } from '../userHooks';
import { useGetEditorThread } from './useGetEditorThread';
import {
  upsertEditorArtifact,
  upsertEditorThreadMessage,
} from 'src/store/updateQueries';
import {
  AvailableModels,
  EditorMessage,
  EditorGraphInput,
  isArtifactCode,
  EditorHandleProps,
} from 'src/types';
import { editorBaseUrl } from 'src/store/constants';
import {
  extractStreamDataChunk,
  getRewrittenArtifact,
  handleGenerateArtifactToolCallChunk,
  removeCodeBlockFormatting,
  updateHighlightedCode,
  updateHighlightedMarkdown,
} from 'src/utils/editorUtils/editorStreaming';
import { useInsertStreamableContentToEditor } from './useInsertStreamableContentToEditor';

export const useRunEditorHelpers = () => {
  const dispatch = useAppDispatch();
  const { user_id } = useGetUser();
  const { editorThread } = useGetEditorThread();
  const { onInsertStreamableContent } = useInsertStreamableContentToEditor();

  const { thread_id } = editorThread ?? {};

  const dispatchUserMessage = useCallback(
    (userMessage: EditorMessage) => {
      if (!thread_id) {
        return;
      }

      dispatch(
        upsertEditorThreadMessage({
          user_id,
          thread_id,
          message: {
            ...userMessage,
            id: uuid_v4(),
          },
          isStreaming: false,
        }),
      );
    },
    [thread_id, user_id, dispatch],
  );

  const dispatchAgentMessage = useCallback(
    ({
      message,
      isStreaming,
    }: {
      message: EditorMessage;
      isStreaming: boolean;
    }) => {
      if (!thread_id || !message?.id) {
        return;
      }

      dispatch(
        upsertEditorThreadMessage({
          user_id,
          thread_id,
          message: { id: message.id, content: message.content },
          isStreaming,
        }),
      );
    },
    [dispatch, user_id, thread_id],
  );

  const fetchStreamReader = useCallback(
    async ({ input, token }: { input: EditorGraphInput; token: string }) => {
      if (!user_id || !thread_id) {
        return;
      }

      const streamURL = `${editorBaseUrl}/users/${user_id}/threads/${thread_id}/runs/stream`;

      const body = {
        input: {
          ...input,
          model_id: AvailableModels.ALIAS_ANTHROPIC_CLAUDE_3_5_SONNET,
        },
        stream_mode: 'events',
      };

      const response = await fetch(streamURL, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('ReadableStream not supported by this environment');
      }

      return reader;
    },

    [user_id, thread_id],
  );

  const handleGenerateArtifact = useCallback(
    ({ parsedData, localState }: EditorHandleProps) => {
      if (!thread_id) {
        return;
      }

      const nodeChunk = parsedData?.data?.chunk;

      const message = extractStreamDataChunk(nodeChunk);

      if (
        message?.tool_call_chunks?.length > 0 &&
        typeof message?.tool_call_chunks?.[0]?.args === 'string'
      ) {
        localState.generateArtifactToolCallStr +=
          message.tool_call_chunks[0].args;
      } else if (message?.content && typeof message?.content === 'string') {
        localState.generateArtifactToolCallStr += message.content;
      }

      const artifactOutputData = handleGenerateArtifactToolCallChunk(
        localState.generateArtifactToolCallStr,
      );

      if (artifactOutputData && typeof artifactOutputData === 'object') {
        dispatch(
          upsertEditorArtifact({
            user_id,
            thread_id,
            artifactOutput: artifactOutputData,
          }),
        );
        onInsertStreamableContent(artifactOutputData);
      }
    },
    [thread_id, user_id, dispatch, onInsertStreamableContent],
  );

  const handleRewriteArtifact = useCallback(
    ({ parsedData, localState, currentArtifactOutput }: EditorHandleProps) => {
      if (
        !localState.rewriteArtifactMeta ||
        !thread_id ||
        !currentArtifactOutput
      ) {
        return;
      }

      const nodeChunk = parsedData?.data?.chunk;

      localState.fullNewArtifactContent +=
        extractStreamDataChunk(nodeChunk)?.content || '';

      const rewrittenArtifactOutput = getRewrittenArtifact({
        prevArtifactOutput: currentArtifactOutput,
        newContent: localState.fullNewArtifactContent,
        rewriteArtifactMeta: localState.rewriteArtifactMeta,
      });

      dispatch(
        upsertEditorArtifact({
          user_id,
          thread_id,
          artifactOutput: rewrittenArtifactOutput,
        }),
      );
      onInsertStreamableContent(rewrittenArtifactOutput);
    },
    [user_id, thread_id, dispatch, onInsertStreamableContent],
  );

  const handleUpdateHighlightedCode = useCallback(
    ({
      parsedData,
      localState,
      currentArtifactOutput,
      highlightedCode,
    }: EditorHandleProps) => {
      if (!thread_id || !currentArtifactOutput) {
        return;
      }

      const nodeChunk = parsedData?.data?.chunk;
      if (!highlightedCode) {
        return;
      }

      const { startCharIndex, endCharIndex } = highlightedCode;

      const prevArtifactContent =
        currentArtifactOutput.contents[
          currentArtifactOutput.contents.length - 1
        ];

      if (!isArtifactCode(prevArtifactContent)) {
        return;
      }

      const partialUpdatedContent =
        extractStreamDataChunk(nodeChunk)?.content || '';

      if (
        localState.updatedArtifactStartContent === undefined &&
        localState.updatedArtifactRestContent === undefined
      ) {
        const prevCode = prevArtifactContent.code;
        localState.updatedArtifactStartContent = prevCode.slice(
          0,
          startCharIndex,
        );
        localState.updatedArtifactRestContent = prevCode.slice(endCharIndex);
      }

      localState.updatedArtifactStartContent += partialUpdatedContent;

      const newContent = removeCodeBlockFormatting(
        `${localState.updatedArtifactStartContent}${localState.updatedArtifactRestContent}`,
      );

      const updatedArtifactOutput = updateHighlightedCode({
        prevArtifactOutput: currentArtifactOutput,
        newContent,
        language: prevArtifactContent.language,
      });

      dispatch(
        upsertEditorArtifact({
          user_id,
          thread_id,
          artifactOutput: updatedArtifactOutput,
        }),
      );
      onInsertStreamableContent(updatedArtifactOutput);
    },
    [thread_id, user_id, dispatch, onInsertStreamableContent],
  );

  const handleUpdateHighlightedText = useCallback(
    ({ parsedData, localState, currentArtifactOutput }: EditorHandleProps) => {
      if (!thread_id || !localState.highlightedText || !currentArtifactOutput) {
        return;
      }

      const nodeChunk = parsedData?.data?.chunk;

      const partialUpdatedContent =
        extractStreamDataChunk(nodeChunk)?.content || '';

      const { fullMarkdown, markdownBlock } = localState.highlightedText;
      const startIndex = fullMarkdown.indexOf(markdownBlock);

      if (
        localState.updatedArtifactStartContent === undefined &&
        localState.updatedArtifactRestContent === undefined
      ) {
        localState.updatedArtifactStartContent = fullMarkdown.slice(
          0,
          startIndex,
        );
        localState.updatedArtifactRestContent = fullMarkdown.slice(
          startIndex + markdownBlock.length,
        );
      }

      localState.updatedArtifactStartContent += partialUpdatedContent;
      const updatedArtifactOutput = updateHighlightedMarkdown({
        prevArtifactOutput: currentArtifactOutput,
        newContent: `${localState.updatedArtifactStartContent}${localState.updatedArtifactRestContent}`,
      });

      dispatch(
        upsertEditorArtifact({
          user_id,
          thread_id,
          artifactOutput: updatedArtifactOutput,
        }),
      );
      onInsertStreamableContent(updatedArtifactOutput);
    },
    [thread_id, user_id, dispatch, onInsertStreamableContent],
  );

  const handleOnChainStart = useCallback(
    ({ parsedData, localState }: EditorHandleProps) => {
      const langgraphNode = parsedData?.metadata?.langgraph_node;
      const nodeInput = parsedData?.data?.input;

      if (langgraphNode === 'updateHighlightedText') {
        localState.highlightedText = nodeInput?.highlightedText;
      }
    },
    [],
  );

  const handleOnChatModelStream = useCallback(
    ({
      parsedData,
      localState,
      currentArtifactOutput,
      highlightedCode,
    }: EditorHandleProps) => {
      const langgraphNode = parsedData?.metadata?.langgraph_node;
      const taskName = parsedData?.name;
      const nodeChunk = parsedData?.data?.chunk;

      if (['generateFollowup', 'replyToGeneralInput'].includes(langgraphNode)) {
        const message = extractStreamDataChunk(nodeChunk) as EditorMessage;

        dispatchAgentMessage({
          message,
          isStreaming: true,
        });
      }

      if (langgraphNode === 'generateArtifact') {
        handleGenerateArtifact({
          parsedData,
          localState,
          currentArtifactOutput,
        });
      }

      if (
        langgraphNode === 'updateHighlightedText' &&
        localState.highlightedText &&
        currentArtifactOutput
      ) {
        handleUpdateHighlightedText({
          parsedData,
          localState,
          currentArtifactOutput,
        });
      }

      if (langgraphNode === 'updateHighlightedCode' && currentArtifactOutput) {
        handleUpdateHighlightedCode({
          parsedData,
          localState,
          currentArtifactOutput,
          highlightedCode,
        });
      }

      if (
        langgraphNode === 'rewriteArtifact' &&
        taskName === 'rewrite_artifact_model_call' &&
        localState.rewriteArtifactMeta
      ) {
        handleRewriteArtifact({
          parsedData,
          localState,
          currentArtifactOutput,
        });
      }
    },
    [
      dispatchAgentMessage,
      handleRewriteArtifact,
      handleUpdateHighlightedCode,
      handleUpdateHighlightedText,
      handleGenerateArtifact,
    ],
  );

  const handleOnChatModelEnd = useCallback(
    ({ parsedData, localState, currentArtifactOutput }: EditorHandleProps) => {
      const langgraphNode = parsedData?.metadata?.langgraph_node;
      const nodeOutput = parsedData?.data?.output;

      if (['generateFollowup', 'replyToGeneralInput'].includes(langgraphNode)) {
        const message = extractStreamDataChunk(nodeOutput);

        dispatchAgentMessage({ message, isStreaming: false });
      }

      if (
        langgraphNode === 'updateHighlightedText' &&
        localState.highlightedText &&
        currentArtifactOutput
      ) {
        handleUpdateHighlightedText({
          parsedData,
          localState,
          currentArtifactOutput,
        });
      }
    },
    [dispatchAgentMessage, handleUpdateHighlightedText],
  );

  const handleOnChainEnd = useCallback(
    ({ parsedData, localState }: EditorHandleProps) => {
      const langgraphNode = parsedData?.metadata?.langgraph_node;
      const taskName = parsedData?.name;
      const nodeOutput = parsedData?.data?.output;

      if (
        langgraphNode === 'rewriteArtifact' &&
        (taskName === 'optionally_update_artifact_meta' ||
          taskName === 'RunnableLambda')
      ) {
        localState.rewriteArtifactMeta = nodeOutput;
      }
    },
    [],
  );

  return useMemo(
    () => ({
      dispatchUserMessage,
      dispatchAgentMessage,
      fetchStreamReader,
      onChainStart: handleOnChainStart,
      onChatModelStream: handleOnChatModelStream,
      onChatModelEnd: handleOnChatModelEnd,
      onChainEnd: handleOnChainEnd,
    }),
    [
      dispatchUserMessage,
      dispatchAgentMessage,
      fetchStreamReader,
      handleOnChainStart,
      handleOnChatModelStream,
      handleOnChatModelEnd,
      handleOnChainEnd,
    ],
  );
};
